import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Search, ChefHat, Plus, X, Check, AlertCircle,
  ArrowRight, Beaker, Save
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Unit conversion map: input unit → { base_unit, factor }
const UNIT_CONVERSIONS = {
  // Weight
  'kg':    { base: 'kg', factor: 1 },
  'g':     { base: 'kg', factor: 0.001 },
  'gms':   { base: 'kg', factor: 0.001 },
  'mg':    { base: 'kg', factor: 0.000001 },
  // Volume
  'L':     { base: 'L', factor: 1 },
  'ml':    { base: 'L', factor: 0.001 },
  // Count
  'pieces': { base: 'pieces', factor: 1 },
  'pcs':    { base: 'pieces', factor: 1 },
  'packets': { base: 'packets', factor: 1 },
};

const INPUT_UNITS = [
  { value: 'g', label: 'g (grams)' },
  { value: 'kg', label: 'kg' },
  { value: 'mg', label: 'mg' },
  { value: 'ml', label: 'ml' },
  { value: 'L', label: 'L (litres)' },
  { value: 'pieces', label: 'pieces' },
  { value: 'packets', label: 'packets' },
];

function convertToBase(quantity, inputUnit) {
  const conv = UNIT_CONVERSIONS[inputUnit];
  if (!conv) return { base_unit: inputUnit, quantity_per_unit: quantity };
  return { base_unit: conv.base, quantity_per_unit: quantity * conv.factor };
}

function reverseFromBase(baseQty, baseUnit) {
  // Find a nice display unit
  if (baseUnit === 'kg' && baseQty < 1 && baseQty >= 0.001) {
    return { displayQty: baseQty * 1000, displayUnit: 'g' };
  }
  if (baseUnit === 'L' && baseQty < 1 && baseQty >= 0.001) {
    return { displayQty: baseQty * 1000, displayUnit: 'ml' };
  }
  return { displayQty: baseQty, displayUnit: baseUnit };
}

const RecipeEditor = ({ getAuthHeaders, initialMenuItemId = null }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filter, setFilter] = useState('all'); // all | configured | not_configured
  const [searchQuery, setSearchQuery] = useState('');
  const [recipesMap, setRecipesMap] = useState({}); // menu_item_id → recipe
  const [isLoading, setIsLoading] = useState(true);

  // Editor state
  const [editingItem, setEditingItem] = useState(null); // menu item being edited
  const [ingredients, setIngredients] = useState([]); // current recipe ingredients
  const [isSaving, setIsSaving] = useState(false);

  // Add ingredient state
  const [addingIngredient, setAddingIngredient] = useState(false);
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [selectedInvItem, setSelectedInvItem] = useState(null);
  const [ingredientQty, setIngredientQty] = useState('');
  const [ingredientUnit, setIngredientUnit] = useState('g');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [menuRes, invRes, recipesRes] = await Promise.all([
        axios.get(`${API}/menu-items`, getAuthHeaders()),
        axios.get(`${API}/inventory/catalog`, getAuthHeaders()),
        axios.get(`${API}/inventory/recipe-mappings`, getAuthHeaders())
      ]);

      setMenuItems(menuRes.data.menu_items || menuRes.data || []);
      setInventoryItems(invRes.data.items || []);

      // Build recipes map
      const rMap = {};
      for (const r of (recipesRes.data.mappings || [])) {
        rMap[r.menu_item_id] = r;
      }
      setRecipesMap(rMap);
    } catch (error) {
      toast.error('Failed to load data');
    }
    setIsLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-open the recipe editor if initialMenuItemId is provided
  useEffect(() => {
    if (initialMenuItemId && menuItems.length > 0) {
      const item = menuItems.find(m =>
        (m.item_id || m.id) === initialMenuItemId
      );
      if (item) openEditor(item);
    }
  }, [initialMenuItemId, menuItems]);

  const openEditor = (menuItem) => {
    const itemId = menuItem.item_id || menuItem.id;
    setEditingItem({ ...menuItem, _id_key: itemId });

    // Load existing recipe
    const existing = recipesMap[itemId];
    if (existing && existing.ingredients) {
      // Convert base units back to display units for editing
      setIngredients(existing.ingredients.map(ing => {
        const { displayQty, displayUnit } = reverseFromBase(
          ing.quantity_per_unit, ing.base_unit
        );
        return {
          ...ing,
          display_qty: displayQty,
          display_unit: displayUnit
        };
      }));
    } else {
      setIngredients([]);
    }
    setAddingIngredient(false);
  };

  const addIngredient = () => {
    if (!selectedInvItem || !ingredientQty) {
      toast.error('Select an item and enter quantity');
      return;
    }
    const qty = parseFloat(ingredientQty);
    if (isNaN(qty) || qty <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }

    // Check if already added
    if (ingredients.some(i => i.inventory_item_id === selectedInvItem.item_id)) {
      toast.error('This ingredient is already added');
      return;
    }

    const { base_unit, quantity_per_unit } = convertToBase(qty, ingredientUnit);

    setIngredients([...ingredients, {
      inventory_item_id: selectedInvItem.item_id,
      item_name: selectedInvItem.item_name,
      quantity_per_unit,
      base_unit,
      display_qty: qty,
      display_unit: ingredientUnit
    }]);

    // Reset
    setSelectedInvItem(null);
    setIngredientQty('');
    setIngredientUnit('g');
    setIngredientSearch('');
    setAddingIngredient(false);
  };

  const removeIngredient = (idx) => {
    setIngredients(ingredients.filter((_, i) => i !== idx));
  };

  const updateIngredientQty = (idx, newQty, newUnit) => {
    const qty = parseFloat(newQty);
    if (isNaN(qty) || qty <= 0) return;
    const { base_unit, quantity_per_unit } = convertToBase(qty, newUnit);
    setIngredients(ingredients.map((ing, i) =>
      i === idx ? { ...ing, quantity_per_unit, base_unit, display_qty: qty, display_unit: newUnit } : ing
    ));
  };

  const saveRecipe = async () => {
    if (!editingItem) return;
    setIsSaving(true);
    try {
      const payload = {
        menu_item_id: editingItem._id_key,
        ingredients: ingredients.map(({ inventory_item_id, item_name, quantity_per_unit, base_unit }) => ({
          inventory_item_id, item_name, quantity_per_unit, base_unit
        }))
      };
      await axios.post(`${API}/inventory/recipe-mapping`, payload, getAuthHeaders());
      toast.success(`Recipe saved for ${editingItem.name}`);

      // Update local map
      setRecipesMap(prev => ({
        ...prev,
        [editingItem._id_key]: { ...payload, client_id: '' }
      }));
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to save recipe');
    }
    setIsSaving(false);
  };

  // Filter menu items
  const filteredMenuItems = menuItems.filter(item => {
    const itemId = item.item_id || item.id;
    const hasRecipe = recipesMap[itemId] && recipesMap[itemId].ingredients?.length > 0;
    const matchesSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;
    if (filter === 'configured') return hasRecipe;
    if (filter === 'not_configured') return !hasRecipe;
    return true;
  });

  const configuredCount = menuItems.filter(m => {
    const id = m.item_id || m.id;
    return recipesMap[id] && recipesMap[id].ingredients?.length > 0;
  }).length;

  // Filtered inventory for ingredient search
  const filteredInventory = inventoryItems.filter(i =>
    i.item_name?.toLowerCase().includes(ingredientSearch.toLowerCase())
  );

  if (isLoading) {
    return <div className="text-center py-12 text-muted-foreground">Loading recipes...</div>;
  }

  return (
    <div data-testid="recipe-editor-section" className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-border"><CardContent className="p-3">
          <div className="text-xs text-muted-foreground">Total Items</div>
          <div className="text-xl font-bold text-primary">{menuItems.length}</div>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-3">
          <div className="text-xs text-muted-foreground">Recipes Set</div>
          <div className="text-xl font-bold text-green-600">{configuredCount}</div>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-3">
          <div className="text-xs text-muted-foreground">Not Configured</div>
          <div className="text-xl font-bold text-amber-600">{menuItems.length - configuredCount}</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input data-testid="recipe-search" placeholder="Search menu items..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-10 rounded-xl" />
        </div>
        <div className="flex bg-muted/50 rounded-xl p-0.5">
          {[
            { key: 'all', label: 'All' },
            { key: 'configured', label: 'Configured' },
            { key: 'not_configured', label: 'Not Set' }
          ].map(f => (
            <Button key={f.key} variant={filter === f.key ? 'default' : 'ghost'} size="sm"
              onClick={() => setFilter(f.key)} data-testid={`recipe-filter-${f.key}`}
              className="rounded-lg text-xs px-3">
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Menu Items List */}
      <div className="space-y-2">
        {filteredMenuItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No menu items match your filter</div>
        ) : (
          filteredMenuItems.map(item => {
            const itemId = item.item_id || item.id;
            const recipe = recipesMap[itemId];
            const hasRecipe = recipe && recipe.ingredients?.length > 0;
            return (
              <Card key={itemId} className={`border-border cursor-pointer hover:shadow-sm transition-shadow ${hasRecipe ? 'border-l-4 border-l-green-500' : ''}`}
                onClick={() => openEditor(item)} data-testid={`recipe-menu-item-${itemId}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0">
                    {item.image?.data ? (
                      <img src={item.image.data} alt="" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <ChefHat className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{item.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className="text-xs py-0">{item.category}</Badge>
                      <span className="text-xs text-muted-foreground">Rs.{item.price}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasRecipe ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <Check className="w-3 h-3 mr-1" />
                        {recipe.ingredients.length} ingredients
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 text-xs">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        No recipe
                      </Badge>
                    )}
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Recipe Editor Dialog */}
      <Dialog open={!!editingItem} onOpenChange={(open) => { if (!open) setEditingItem(null); }}>
        <DialogContent className="bg-surface max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Beaker className="w-5 h-5 text-primary" />
              Recipe — {editingItem?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="text-xs text-muted-foreground mb-3">
            Define how much of each inventory item is consumed when 1 unit of "{editingItem?.name}" is sold.
          </div>

          {/* Current Ingredients */}
          <div className="space-y-2 mb-4">
            {ingredients.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
                No ingredients added yet
              </div>
            ) : (
              ingredients.map((ing, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded-xl" data-testid={`ingredient-row-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{ing.item_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {ing.display_qty} {ing.display_unit}
                      {ing.display_unit !== ing.base_unit && (
                        <span className="ml-1 text-primary">
                          = {ing.quantity_per_unit.toFixed(4)} {ing.base_unit}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.01" min="0.01" value={ing.display_qty}
                      onChange={(e) => updateIngredientQty(idx, e.target.value, ing.display_unit)}
                      className="w-16 h-8 text-xs rounded-lg text-center" data-testid={`ingredient-qty-${idx}`} />
                    <Select value={ing.display_unit} onValueChange={(v) => updateIngredientQty(idx, ing.display_qty, v)}>
                      <SelectTrigger className="w-20 h-8 text-xs rounded-lg" data-testid={`ingredient-unit-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {INPUT_UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeIngredient(idx)}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700" data-testid={`remove-ingredient-${idx}`}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>

          {/* Add Ingredient */}
          {addingIngredient ? (
            <div className="border border-primary/30 rounded-xl p-3 space-y-3 bg-primary/5">
              <div className="text-sm font-medium">Add Ingredient</div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input data-testid="ingredient-search-input" placeholder="Search inventory items..."
                  value={ingredientSearch} onChange={(e) => setIngredientSearch(e.target.value)}
                  className="pl-8 h-9 rounded-lg text-sm" autoFocus />
              </div>
              {ingredientSearch && (
                <div className="max-h-36 overflow-y-auto border border-border rounded-lg">
                  {filteredInventory.length === 0 ? (
                    <div className="p-2 text-xs text-muted-foreground text-center">No items found</div>
                  ) : (
                    filteredInventory.map(inv => (
                      <div key={inv.item_id}
                        className={`p-2 text-sm cursor-pointer hover:bg-muted/50 flex items-center justify-between ${selectedInvItem?.item_id === inv.item_id ? 'bg-primary/10' : ''}`}
                        onClick={() => setSelectedInvItem(inv)} data-testid={`inv-option-${inv.item_id}`}>
                        <span className="truncate">{inv.item_name}</span>
                        <Badge variant="outline" className="text-xs ml-2 flex-shrink-0">{inv.base_unit}</Badge>
                      </div>
                    ))
                  )}
                </div>
              )}
              {selectedInvItem && (
                <div className="flex items-center gap-2">
                  <div className="text-xs text-primary font-medium flex-shrink-0">{selectedInvItem.item_name}</div>
                  <Input type="number" step="0.01" min="0.01" placeholder="Qty" value={ingredientQty}
                    onChange={(e) => setIngredientQty(e.target.value)}
                    className="w-20 h-8 text-xs rounded-lg" data-testid="new-ingredient-qty" />
                  <Select value={ingredientUnit} onValueChange={setIngredientUnit}>
                    <SelectTrigger className="w-24 h-8 text-xs rounded-lg" data-testid="new-ingredient-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INPUT_UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={addIngredient} className="h-8 rounded-lg" data-testid="confirm-add-ingredient">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              )}
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => { setAddingIngredient(false); setSelectedInvItem(null); setIngredientSearch(''); setIngredientQty(''); }}
                  className="text-xs">Cancel</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setAddingIngredient(true)} className="w-full h-9 rounded-xl text-sm" data-testid="add-ingredient-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Ingredient
            </Button>
          )}

          {/* Save */}
          <div className="flex gap-2 mt-4 pt-3 border-t border-border">
            <Button variant="outline" onClick={() => setEditingItem(null)} className="flex-1 h-10 rounded-xl">
              Cancel
            </Button>
            <Button onClick={saveRecipe} disabled={isSaving} className="flex-1 h-10 rounded-xl font-bold bg-primary text-white" data-testid="save-recipe-btn">
              <Save className="w-4 h-4 mr-1" />
              {isSaving ? 'Saving...' : 'Save Recipe'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecipeEditor;
